import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { Check } from 'lucide-react';

const tiers = [
  {
    name: 'Essential Scribe',
    price: '$199',
    period: '/provider/month',
    badge: null,
    highlighted: false,
    features: [
      'Ambient AI Transcription',
      'HIPAA-Compliant Medical Notes',
      'EHR Integration (Read-only)',
      'Multilingual Support',
      'Email Support',
      '3-month free trial',
    ],
  },
  {
    name: 'Revenue Max',
    price: '$449',
    period: '/provider/month',
    badge: 'Most Popular',
    highlighted: true,
    features: [
      'Everything in Essential Scribe',
      'Automated ICD-10 & CPT Medical Coding',
      'Insurance Claim Pre-verification',
      'Revenue Leakage Dashboard',
      'Epic & Cerner Full Integration',
      'Priority Support',
      '3-month free trial',
    ],
  },
];

export default function Pricing() {
  const [ref, isVisible] = useScrollAnimation(0.1);

  const scrollToDemo = () => {
    const el = document.querySelector('#demo-form');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="pricing" data-testid="pricing-section" className="py-24 md:py-32 px-4 md:px-8">
      <div className="max-w-5xl mx-auto">
        <div ref={ref} className={`scroll-fade-up ${isVisible ? 'visible' : ''}`}>
          <p className="uppercase text-xs tracking-[0.2em] text-vf-forest font-body font-semibold mb-4 text-center">
            Pricing
          </p>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-vf-charcoal tracking-tight mb-4 text-center leading-tight">
            Simple, transparent pricing.
          </h2>
          <p className="font-body text-base md:text-lg text-vf-charcoal/60 text-center max-w-xl mx-auto mb-16">
            No hidden fees. No hardware costs. Cancel anytime.
          </p>
        </div>

        <div className={`stagger-children ${isVisible ? 'visible' : ''} grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto`}>
          {tiers.map((tier, i) => (
            <div
              key={tier.name}
              data-testid={`pricing-tier-${i + 1}`}
              className={`relative border overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-sm ${
                tier.highlighted
                  ? 'border-vf-forest'
                  : 'border-vf-sage bg-white'
              }`}
            >
              {/* Header */}
              <div className={`p-8 ${tier.highlighted ? 'bg-vf-forest' : 'bg-vf-salt'}`}>
                {tier.badge && (
                  <span className="inline-block bg-vf-mint text-vf-charcoal font-body text-xs font-bold uppercase tracking-[0.15em] px-3 py-1 mb-4">
                    {tier.badge}
                  </span>
                )}
                <h3 className={`font-heading font-bold text-2xl mb-2 ${
                  tier.highlighted ? 'text-white' : 'text-vf-charcoal'
                }`}>
                  {tier.name}
                </h3>
                <div className="flex items-baseline gap-1">
                  <span className={`font-heading font-bold text-4xl ${
                    tier.highlighted ? 'text-vf-mint' : 'text-vf-forest'
                  }`}>
                    {tier.price}
                  </span>
                  <span className={`font-body text-sm ${
                    tier.highlighted ? 'text-white/60' : 'text-vf-charcoal/50'
                  }`}>
                    {tier.period}
                  </span>
                </div>
              </div>

              {/* Features */}
              <div className="p-8 bg-white">
                <ul className="space-y-3">
                  {tier.features.map((feat) => (
                    <li key={feat} className="flex items-start gap-3">
                      <Check size={16} strokeWidth={2.5} className="text-vf-forest mt-0.5 shrink-0" />
                      <span className={`font-body text-sm text-vf-charcoal/80 ${
                        feat.includes('Automated ICD-10') || feat.includes('Automated Coding')
                          ? 'font-bold text-vf-forest'
                          : ''
                      }`}>
                        {feat}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={scrollToDemo}
                  className={`mt-8 w-full font-body text-sm font-semibold py-3 transition-all duration-200 ${
                    tier.highlighted
                      ? 'bg-vf-mint text-vf-charcoal hover:bg-vf-mint/80'
                      : 'bg-vf-forest text-vf-salt hover:bg-vf-forest/90'
                  }`}
                >
                  Start Free Trial
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Funding note */}
        <p className={`scroll-fade-up ${isVisible ? 'visible' : ''} text-center font-body text-sm text-vf-charcoal/50 mt-10 max-w-lg mx-auto`}>
          We'll help you navigate RHTP subaward funding.{' '}
          <button
            onClick={scrollToDemo}
            className="text-vf-forest font-semibold underline underline-offset-2 hover:text-vf-forest/80"
          >
            Ask us how.
          </button>
        </p>
      </div>
    </section>
  );
}
